import { ReactElement, useState } from "react";
import {
  Typography,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Box,
  Divider,
  Button,
} from "@mui/material";

export interface FeatureSelectionFormProps {
  onSubmit?: (data: FeatureSelectionData) => void;
  onBack?: () => void;
  onContinue?: () => void;
  className?: string;
}

export interface FeatureSelectionData {
  n_top_genes: number;
  flavor: string;
}

const FeatureSelectionForm = ({
  onSubmit,
  onBack,
  onContinue,
  className = "",
}: FeatureSelectionFormProps): ReactElement => {
  const [formData, setFormData] = useState<FeatureSelectionData>({
    n_top_genes: 2000,
    flavor: "seurat",
  });

  const handleInputChange = (
    field: keyof FeatureSelectionData,
    value: string | number,
  ) => {
    setFormData((prev) => ({
      ...prev,
      [field]: value as any,
    }));
  };

  const handleSubmit = () => {
    onSubmit?.(formData);
  };

  return (
    <div
      className={`p-6 h-full flex flex-col justify-between ${className}`}
      style={{
        backgroundColor: "#F5F5F5", // tokens.colors.gray[100]
      }}
    >
      {/* Description */}
      <Box>
        <Typography
          variant="body2"
          className="text-gray-600 mb-4 leading-relaxed"
          sx={{ fontSize: "0.875rem", lineHeight: 1.6 }}
        >
          Feature selection identifies the most variable genes in your dataset,
          which are typically the most informative for downstream analysis. This
          step helps reduce noise and computational complexity while preserving
          the most biologically relevant information.
        </Typography>

        <Typography
          variant="body2"
          color="text.secondary"
          className="mb-6 leading-relaxed"
          sx={{ fontSize: "0.875rem", lineHeight: 1.6 }}
        >
          Configure the parameters for feature selection:
        </Typography>
      </Box>

      <Divider className="mb-6" />

      {/* Form Fields */}
      <div className="space-y-6 flex-1">
        <TextField
          fullWidth
          label="Number of Top Genes"
          type="number"
          value={formData.n_top_genes}
          onChange={(e) => {
            const value = e.target.value;
            if (value === "") {
              handleInputChange("n_top_genes", 0);
            } else {
              const numValue = parseInt(value);
              if (!isNaN(numValue) && numValue >= 1) {
                handleInputChange("n_top_genes", numValue);
              }
            }
          }}
          inputProps={{
            min: 1,
            step: 1,
          }}
          helperText="Number of highly variable genes to select (default: 2000)"
          sx={{
            backgroundColor: "#FFFFFF",
            "& .MuiOutlinedInput-root": {
              "& fieldset": {
                borderColor: "#D9D9D9",
              },
              "&:hover fieldset": {
                borderColor: "#19C1DB",
              },
              "&.Mui-focused fieldset": {
                borderColor: "#19C1DB",
              },
            },
          }}
        />

        <FormControl fullWidth>
          <InputLabel id="flavor-label">Flavor</InputLabel>
          <Select
            labelId="flavor-label"
            value={formData.flavor}
            label="Flavor"
            onChange={(e) => handleInputChange("flavor", e.target.value)}
            sx={{
              backgroundColor: "#FFFFFF",
              "& .MuiOutlinedInput-notchedOutline": {
                borderColor: "#D9D9D9",
              },
              "&:hover .MuiOutlinedInput-notchedOutline": {
                borderColor: "#19C1DB",
              },
              "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                borderColor: "#19C1DB",
              },
            }}
          >
            <MenuItem value="seurat">Seurat</MenuItem>
            <MenuItem value="cell_ranger">Cell Ranger</MenuItem>
            <MenuItem value="seurat_v3">Seurat v3</MenuItem>
            <MenuItem value="seurat_v3_paper">Seurat v3 Paper</MenuItem>
          </Select>
        </FormControl>
      </div>

      {/* Action Buttons */}
      <div className="flex justify-between gap-3 mt-6">
        <Button
          variant="outlined"
          color="primary"
          onClick={onBack}
          sx={{
            textTransform: "none",
            fontWeight: 500,
            px: 3,
            py: 1.5,
          }}
        >
          Back
        </Button>
        <Button
          variant="contained"
          onClick={handleSubmit}
          className="bg-[#19C1DB] hover:bg-[#17B1CB] text-white"
          sx={{
            textTransform: "none",
            fontWeight: 500,
            px: 3,
            py: 1.5,
          }}
        >
          Submit
        </Button>
      </div>
    </div>
  );
};

export default FeatureSelectionForm;
